import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import EmpresasPage from './EmpresasPage';
import PersonasPage from './PersonasPage';
import CatalogosPage from './CatalogosPage';
import UsuariosPage from './UsuariosPage';
import { esAdministrador } from '../utils/usuarioActual';

import {
  obtenerConfiguracionPorClave,
  actualizarConfiguracion
} from '../services/configuracionService';

function ReglaCard({ titulo, children }) {
  return (
    <div
      className="card-box mb-3"
      style={{
        borderLeft: '5px solid #0d6efd',
        background: '#ffffff'
      }}
    >
      <h6 className="fw-bold mb-2">
        {titulo}
      </h6>

      <div
        className="text-muted"
        style={{
          fontSize: '14px',
          lineHeight: '1.55'
        }}
      >
        {children}
      </div>
    </div>
  );
}

function ConfiguracionSistemaTab() {
  const [diasParaPendiente, setDiasParaPendiente] = useState('3');
  const [descripcion, setDescripcion] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    cargarConfiguracion();
  }, []);

  async function cargarConfiguracion() {
    try {
      setCargando(true);
      setMensaje('');

      const data = await obtenerConfiguracionPorClave(
        'DiasParaPendiente'
      );

      setDiasParaPendiente(data.Valor || '3');
      setDescripcion(data.Descripcion || '');
    } catch (error) {
      setMensaje(
        error.response?.data?.mensaje ||
        'No fue posible cargar la configuración.'
      );
    } finally {
      setCargando(false);
    }
  }

  async function guardarConfiguracion(e) {
    e.preventDefault();

    const numero = Number(diasParaPendiente);

    if (
      !Number.isInteger(numero) ||
      numero < 1 ||
      numero > 365
    ) {
      setMensaje(
        'Captura un número entero entre 1 y 365.'
      );

      return;
    }

    try {
      setGuardando(true);
      setMensaje('');

      await actualizarConfiguracion(
        'DiasParaPendiente',
        numero
      );

      setMensaje(
        'Configuración guardada correctamente.'
      );
    } catch (error) {
      setMensaje(
        error.response?.data?.mensaje ||
        'No fue posible guardar la configuración.'
      );
    } finally {
      setGuardando(false);
    }
  }

  if (cargando) {
    return (
      <div className="card-box">
        Cargando configuración...
      </div>
    );
  }

  return (
    <div>

      <div className="card-box mb-4">

        <h5 className="mb-4">
          Reglas internas del sistema
        </h5>

        {mensaje && (
          <div className="alert alert-info">
            {mensaje}
          </div>
        )}

        <form onSubmit={guardarConfiguracion}>

          <div className="row">

            <div className="col-md-4">

              <label className="form-label">
                Días para marcar como pendiente
              </label>

              <input
                type="number"
                className="form-control"
                value={diasParaPendiente}
                onChange={(e) =>
                  setDiasParaPendiente(e.target.value)
                }
                min="1"
                max="365"
              />

              <div className="form-text">
                Días después de emitido para mostrar un recibo como pendiente
                si no tiene PDF firmado ni está cancelado.
              </div>

              {descripcion && (
                <div className="form-text">
                  {descripcion}
                </div>
              )}

            </div>

          </div>

          <div className="mt-4">

            <button
              type="submit"
              className="btn btn-primary"
              disabled={guardando}
            >
              {guardando
                ? 'Guardando...'
                : 'Guardar configuración'}
            </button>

          </div>

        </form>

      </div>

      <div className="row">

        <div className="col-md-6">
          <ReglaCard titulo="1. Empresa Emite">
            La Empresa Emite debe estar registrada en el catálogo de Empresas.
            Es obligatoria porque define identidad del recibo, prefijo de folio,
            logo, color de línea, revisión del formato y datos fiscales del PDF.
          </ReglaCard>
        </div>

        <div className="col-md-6">
          <ReglaCard titulo="2. Persona Emite">
            La Persona Emite representa a quien entrega, autoriza o firma por
            parte de la empresa emisora. Debe seleccionarse desde Personas con
            tipo Emisor para mantener control interno y trazabilidad.
          </ReglaCard>
        </div>

        <div className="col-md-6">
          <ReglaCard titulo="3. Empresa Recibe">
            La Empresa Recibe identifica la empresa beneficiaria del recibo.
            Puede seleccionarse desde Empresas cuando aplique. Si el recibo es
            para una persona física sin empresa, puede usarse la lógica de
            persona ocasional en el campo Persona Recibe.
          </ReglaCard>
        </div>

        <div className="col-md-6">
          <ReglaCard titulo="4. Persona Recibe">
            La Persona Recibe puede seleccionarse desde el catálogo de Personas
            tipo Receptor. Para pagos únicos a una persona no registrada, se
            permite capturar una persona ocasional, la cual queda guardada solo
            en ese recibo y no se agrega al catálogo.
          </ReglaCard>
        </div>

        <div className="col-md-6">
          <ReglaCard titulo="5. Tipo de Recibo y Concepto">
            El Tipo de Recibo define el título del documento: “Recibo de Pago”,
            “Recibo de Gastos”, etc. El Concepto es el motivo específico del
            recibo y se filtra automáticamente según el Tipo de Recibo elegido.
          </ReglaCard>
        </div>

        <div className="col-md-6">
          <ReglaCard titulo="6. Estatus automático">
            El usuario no selecciona manualmente el estatus. El sistema calcula:
            Emitido cuando no tiene firmado y no venció el plazo; Pendiente
            cuando superó los días configurados; Pagado cuando tiene PDF firmado;
            Cancelado cuando fue cancelado.
          </ReglaCard>
        </div>

        <div className="col-md-6">
          <ReglaCard titulo="7. Cancelados">
            Un recibo cancelado no suma importes en Dashboard, no cuenta como
            recibo operativo del mes y queda bloqueado para carga o reemplazo de
            firmado. Solo se conserva por trazabilidad y auditoría.
          </ReglaCard>
        </div>

        <div className="col-md-6">
          <ReglaCard titulo="8. PDF firmado">
            Cuando se sube un PDF firmado, el recibo se considera Pagado de forma
            automática. Si se reemplaza el firmado, el movimiento queda registrado
            en Bitácora.
          </ReglaCard>
        </div>

        <div className="col-md-6">
          <ReglaCard titulo="9. Bitácora">
            Las acciones importantes quedan registradas: creación, duplicado,
            cancelación, carga de firmado, reemplazo de firmado y cambios
            administrativos relevantes. El usuario logeado se guarda como
            responsable de la acción.
          </ReglaCard>
        </div>

        <div className="col-md-6">
          <ReglaCard titulo="10. Conducto">
            El Conducto será una Persona registrada con tipo Conducto. No reemplaza
            al Emisor ni al Receptor y no modifica el PDF principal; solo servirá
            para control interno de quién entrega físicamente el dinero o recupera
            el recibo firmado.
          </ReglaCard>
        </div>

      </div>

    </div>
  );
}

function ConfiguracionPage() {
  const [tab, setTab] = useState('catalogos');
  const navigate = useNavigate();
  const usuarioEsAdministrador = esAdministrador();

  return (
    <div className="page-container-normal">

      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h1>Configuración</h1>
        </div>

        <button
          className="btn btn-primary"
          type="button"
          onClick={() => navigate('/recibos')}
        >
          Nuevo recibo
        </button>
      </div>

      <ul className="nav nav-tabs mt-4">

        <li className="nav-item">
          <button
            className={`nav-link ${
              tab === 'catalogos' ? 'active' : ''
            }`}
            onClick={() => setTab('catalogos')}
            type="button"
          >
            Catálogos
          </button>
        </li>

        <li className="nav-item">
          <button
            className={`nav-link ${
              tab === 'empresas' ? 'active' : ''
            }`}
            onClick={() => setTab('empresas')}
            type="button"
          >
            Empresas
          </button>
        </li>

        <li className="nav-item">
          <button
            className={`nav-link ${
              tab === 'personas' ? 'active' : ''
            }`}
            onClick={() => setTab('personas')}
            type="button"
          >
            Personas
          </button>
        </li>

        <li className="nav-item">
          <button
            className={`nav-link ${
              tab === 'reglas' ? 'active' : ''
            }`}
            onClick={() => setTab('reglas')}
            type="button"
          >
            Reglas Sistema
          </button>
        </li>

        {usuarioEsAdministrador && (
          <li className="nav-item">
            <button
              className={`nav-link ${
                tab === 'usuarios' ? 'active' : ''
              }`}
              onClick={() => setTab('usuarios')}
              type="button"
            >
              Usuarios
            </button>
          </li>
        )}

      </ul>

      <div className="mt-4">

        {tab === 'catalogos' && (
          <CatalogosPage />
        )}

        {tab === 'empresas' && (
          <EmpresasPage />
        )}

        {tab === 'personas' && (
          <PersonasPage />
        )}

        {tab === 'reglas' && (
          <ConfiguracionSistemaTab />
        )}

        {tab === 'usuarios' && usuarioEsAdministrador && (
          <UsuariosPage />
        )}

        {tab === 'usuarios' && !usuarioEsAdministrador && (
          <div className="card-box">
            <h5>Acceso restringido</h5>
            <p className="text-muted mb-0">
              Esta sección solo está disponible para usuarios Administrador.
            </p>
          </div>
        )}

      </div>

    </div>
  );
}

export default ConfiguracionPage;